'use client'

import { Text } from '@macarongfactory/web-ui'
import Link from 'next/link'

import { buildCarHubPath } from '@/entities/car'

import MaintenanceReviewList from './MaintenanceReviewList'
import {
  MODOOCAR_CODE,
  useModoocarImpression,
  useModoocarTracking,
} from '../_features/modoocar'

import type { IModoocarDetail } from '../_features/modoocar'

interface IMaintenanceSectionProps {
  detail: IModoocarDetail
  isInApp?: boolean
}

const SECTION_TITLE_SUFFIX = '마이클 정비 리뷰'

const BANNER_GRADIENT = 'linear-gradient(10deg, #0D3E51 0%, #25283B 100%)'
const BANNER_SUBTITLE_COLOR = '#CFD5D9'
const BANNER_GRAPHIC_URL =
  'https://mycle.co.kr/resources/images/contents/img_moodocar_review.png'
const BANNER_GRAPHIC_WIDTH_PX = 80
const BANNER_GRAPHIC_HEIGHT_PX = 108

/** [정책]: 정비 비용 확인은 차종 허브로 보내서 두 페이지가 같은 검색어로 경쟁하지 않게 합니다. */
const buildCarHubCtaHref = (carHubPath: string) =>
  `${carHubPath}?utm_source=modoocar&utm_medium=maintenance_cta`
const buildCarHubReviewHref = (carHubPath: string) =>
  `${carHubPath}?utm_source=modoocar&utm_medium=maintenance_review_more`
const buildMaintenanceReviewDeeplink = ({
  model,
  brand,
}: {
  model: string
  brand: string
}) =>
  `macarongapp:///modoo/maintenances/list/${encodeURIComponent(model)}/${encodeURIComponent(brand)}`

const MaintenanceSection = ({
  detail,
  isInApp = false,
}: IMaintenanceSectionProps) => {
  const {
    brand,
    upperModelName,
    lowerModelName,
    carHubModelName,
    fuelType,
    maintenance,
    isMotorcycle,
  } = detail
  const carHubPath =
    buildCarHubPath({
      brand,
      car: upperModelName,
      model: carHubModelName,
      fuel: fuelType,
    }) ?? maintenance.carHubPath
  const isMotorcycleWeb = Boolean(isMotorcycle) && !isInApp
  // [앱]: 정비 후기 딥링크의 {차량모델}에는 세부모델명을 넣어야 합니다.
  const reviewMoreHref = isInApp
    ? buildMaintenanceReviewDeeplink({ model: lowerModelName, brand })
    : buildCarHubReviewHref(carHubPath)

  const { sectionShow, itemClick } = useModoocarTracking()
  const repairRef = useModoocarImpression<HTMLAnchorElement>(() =>
    sectionShow(MODOOCAR_CODE.REPAIR_PROMOTION, '정비 비용 확인 배너')
  )
  const serviceRef = useModoocarImpression<HTMLElement>(() =>
    sectionShow(MODOOCAR_CODE.SERVICE_REVIEW, '마이클 정비 후기')
  )

  return (
    <div
      id='maintenance'
      className='scroll-mt-[var(--mc-section-scroll-mt,97px)] lg:scroll-mt-[112px]'
    >
      {!isInApp && !isMotorcycle && (
        <Link
          ref={repairRef}
          href={buildCarHubCtaHref(carHubPath)}
          onClick={() =>
            itemClick(
              MODOOCAR_CODE.REPAIR_PROMOTION,
              'button',
              '정비 비용 확인 배너'
            )
          }
          aria-label={`${upperModelName} 연간 정비 비용 확인하기`}
          className='relative flex h-[108px] items-center overflow-hidden px-dimension-x6'
          style={{ background: BANNER_GRADIENT }}
        >
          <div className='relative z-10 flex w-[calc(100%-100px)] flex-col gap-dimension-x1'>
            <Text variant='body-1-medium' color={BANNER_SUBTITLE_COLOR}>
              {maintenance.ownerCount.toLocaleString()}명 오너 실거래 기반
            </Text>
            <div className='flex flex-col'>
              <Text
                variant='title-1-semibold'
                color='text-foreground-static-white'
                className='truncate'
              >
                {`‘${upperModelName}’`}
              </Text>
              <Text
                variant='title-1-semibold'
                color='text-foreground-static-white'
              >
                연간 정비 비용 확인하기
              </Text>
            </div>
          </div>

          <img
            src={BANNER_GRAPHIC_URL}
            alt=''
            aria-hidden
            width={BANNER_GRAPHIC_WIDTH_PX}
            height={BANNER_GRAPHIC_HEIGHT_PX}
            className='pointer-events-none absolute right-dimension-x6 top-0 h-full w-[80px] select-none object-contain'
          />
        </Link>
      )}

      <section
        ref={serviceRef}
        className='flex flex-col gap-dimension-x4 py-dimension-x8'
      >
        <div className='px-dimension-x5'>
          <h2 className='flex min-w-0 items-center'>
            <Text
              as='span'
              variant='title-1-semibold'
              color='text-foreground-neutral-strong'
              className='min-w-0 truncate'
            >
              {upperModelName}
            </Text>
            <Text
              as='span'
              variant='title-1-semibold'
              color='text-foreground-neutral-strong'
              className='shrink-0 whitespace-nowrap'
            >
              &nbsp;{SECTION_TITLE_SUFFIX}&nbsp;
            </Text>
            <Text
              as='span'
              variant='title-1-semibold'
              color='text-foreground-brand-strong'
              className='shrink-0'
            >
              {maintenance.reviewCount.toLocaleString()}
            </Text>
          </h2>
        </div>

        <MaintenanceReviewList
          reviews={maintenance.reviews}
          total={maintenance.reviewCount}
          moreHref={isMotorcycleWeb ? undefined : reviewMoreHref}
          showAppCta={isMotorcycleWeb}
          costHref={
            isInApp || isMotorcycle ? undefined : buildCarHubCtaHref(carHubPath)
          }
        />
      </section>
    </div>
  )
}

export default MaintenanceSection
